import { useState } from 'react';

import { useDrawdownRepaymentSchedule } from '@api/lendingAPI/lapiHooks';
import { TFormalOffer } from '@helpers/FormalOffers/formalOffers.types';
import { tracking } from '@iwoca/frontend-tracking-library';
import { Button, Modal } from '@iwoca/orion';
import { ErrorMessage, Text } from '@iwoca/orion-ui';
import { Skeleton } from '@ui';
import { FormattedMessage } from 'react-intl';

import { isSelectedAmountValid } from './isSelectedAmountValid';

export const SeeRateDetails = ({
    drawdownAmount,
    selectedOffer,
    isPartialDrawdown
}: {
    drawdownAmount: number;
    selectedOffer: TFormalOffer;
    isPartialDrawdown?: boolean;
}) => {
    const [showModal, setShowModal] = useState(false);
    const { data, isLoading, isError } = useDrawdownRepaymentSchedule({
        offerId: selectedOffer.offer_id!,
        drawdownAmount,
        enabled: isSelectedAmountValid(drawdownAmount, selectedOffer)
    });

    if (isLoading) return <Skeleton className="h-[20px] w-[50px]" />;
    if (isError)
        return (
            <ErrorMessage>
                <ErrorMessage.Icon />
                <ErrorMessage.Text>
                    <FormattedMessage
                        id="_.topup_choice.generic_error_message"
                        defaultMessage="Oops, it looks like something went wrong"
                    />
                </ErrorMessage.Text>
            </ErrorMessage>
        );

    const blendedRateDetails = data.summary.blended_rate_details;

    const hasRateChanged =
        blendedRateDetails &&
        blendedRateDetails.rate_on_pre_funding_principal -
            blendedRateDetails.rate_on_post_funding_principal !==
            0;

    const openModal = () => {
        tracking.ampli.blendedRateModalOpened({
            offer_id: selectedOffer.offer_id!
        });
        setShowModal(true);
    };

    if (isPartialDrawdown && !hasRateChanged) return null;

    return (
        <>
            <Modal
                isOpen={showModal}
                onClose={() => setShowModal(false)}
                closeIcon
                className="pt-2xl"
                size="medium"
            >
                <div className="space-y-m">
                    {selectedOffer.is_repayment_reduction ? (
                        <>
                            <Text className="text-l font-bold">
                                <FormattedMessage
                                    id="_.topup_choice.rate_details_title"
                                    defaultMessage="Rate details"
                                />
                            </Text>
                            <Text>
                                <FormattedMessage
                                    id="_.topup_choice.rate_details_description"
                                    defaultMessage="We charge different interest rates for different term lengths, so when you extend or shorten your repayment schedule we may adjust your interest rate."
                                />
                            </Text>
                        </>
                    ) : (
                        <>
                            <Text className="text-l font-bold">
                                <FormattedMessage
                                    id="_.topup_choice.blended_rate_details_title"
                                    defaultMessage="How we determine your rate"
                                />
                            </Text>
                            <ol className="list-outside list-decimal pl-l marker:font-bold">
                                <li
                                    className="font-reg"
                                    data-testid="term-selected-info"
                                >
                                    <FormattedMessage
                                        id="_.topup_choice.blended_rate_details_description"
                                        defaultMessage="<span>The term you’ve selected:</span> we charge different interest rates for different term lengths, so when you extend or shorten your repayment schedule we may adjust your interest rate."
                                        values={{
                                            span: (chunks) => (
                                                <span className="font-bold">
                                                    {chunks}
                                                </span>
                                            )
                                        }}
                                    />
                                </li>
                                <li
                                    className="font-reg"
                                    data-testid="new-funds-rate-info"
                                >
                                    <FormattedMessage
                                        id="_.topup_choice.blended_rate_details_description_two"
                                        defaultMessage="<span>The rate for your new funds:</span> we run new credit checks to determine the rate on your new funds and then combine it with the rate on your existing funds."
                                        values={{
                                            span: (chunks) => (
                                                <span className="font-bold">
                                                    {chunks}
                                                </span>
                                            )
                                        }}
                                    />
                                </li>
                            </ol>
                        </>
                    )}
                </div>
            </Modal>
            <Button
                variant="tertiary"
                className="h-[30px] px-0"
                size="small"
                onClick={openModal}
            >
                <FormattedMessage
                    id="_.topup_choice.blended_rate_details_see_rate_details"
                    defaultMessage="See rate details"
                />
            </Button>
        </>
    );
};
